import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Downloads.css';

const Downloads = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoId = searchParams.get('video_id');
  const highlightId = searchParams.get('highlight');
  const videoSectionId = searchParams.get('video');
  const categoryParam = searchParams.get('category');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [aiLauncherForVideo, setAiLauncherForVideo] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedDownloads, setGroupedDownloads] = useState({});

  useEffect(() => {
    if (categoryParam && selectedCategory !== categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  useEffect(() => {
    if (videoId) {
      fetchData();
    } else {
      fetchCategories();
    }
  }, [videoId]);

  // 有 highlight 时，根据下载项 ID 获取其分类并切换到对应分类视图
  useEffect(() => {
    if (highlightId && selectedCategory === null && !videoId) {
      axios.get(`/api/downloads/${highlightId}`)
        .then((res) => {
          const cat = res.data?.category;
          if (cat) setSelectedCategory(cat);
        })
        .catch(() => {});
    }
  }, [highlightId, videoId]);

  // 有 video 时，定位到「以视频命名的板块」。若无 category 则根据该视频的下载项反推分类
  useEffect(() => {
    if (videoSectionId && !videoId && !categoryParam) {
      axios.get(`/api/downloads?video_id=${videoSectionId}`)
        .then((res) => {
          const list = res.data || [];
          const cat = list[0]?.category;
          if (cat) setSelectedCategory(cat);
        })
        .catch(() => {});
    }
  }, [videoSectionId, videoId, categoryParam]);

  useEffect(() => {
    if (selectedCategory !== null && !videoId) {
      fetchData();
    }
  }, [selectedCategory]);

  // 定位到指定下载项
  useEffect(() => {
    if (highlightId && downloads.length > 0) {
      const el = document.getElementById(`download-${highlightId}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        el.classList.add('highlight-flash');
        const t = setTimeout(() => el.classList.remove('highlight-flash'), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [highlightId, downloads]);

  // 定位到以视频命名的板块（如「第2集★视频动作迁移★...」）
  useEffect(() => {
    if (videoSectionId && Object.keys(groupedDownloads).length > 0) {
      const el = document.getElementById(`video-section-${videoSectionId}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
        el.classList.add('highlight-flash');
        const t = setTimeout(() => el.classList.remove('highlight-flash'), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [videoSectionId, groupedDownloads]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/categories?table=downloads');
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取分类列表失败:', error);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      if (videoId) {
        const [downloadsRes, aiLauncherRes] = await Promise.all([
          axios.get(`/api/downloads?video_id=${videoId}`),
          axios.get(`/api/ai-launcher?video_id=${videoId}`)
        ]);
        setDownloads(downloadsRes.data || []);
        setAiLauncherForVideo(Array.isArray(aiLauncherRes.data) ? aiLauncherRes.data : []);
      } else {
        let url = '/api/downloads';
        if (selectedCategory) {
          url = `/api/downloads?category=${encodeURIComponent(selectedCategory)}`;
        }
        const response = await axios.get(url);
        const downloadsData = response.data;
        setDownloads(downloadsData);
        setAiLauncherForVideo([]);
        const videosResponse = await axios.get('/api/videos');
        setVideos(videosResponse.data);
        
        // 按video_id分组
        const grouped = {};
        downloadsData.forEach((download) => {
          const relatedVideoIds = (download.video_ids && download.video_ids.length > 0)
            ? download.video_ids
            : (download.video_id ? [download.video_id] : ['unassigned']);

          relatedVideoIds.forEach((vid) => {
            const key = vid || 'unassigned';
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(download);
          });
        });
        setGroupedDownloads(grouped);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setDownloads([]);
    setAiLauncherForVideo([]);
    setGroupedDownloads({});
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      '百度网盘': '📦',
      '夸克网盘': '☁️',
      '阿里云盘': '💾',
      '迅雷云盘': '⚡',
      '123云盘': '☁️',
      '直链地址': '🌐',
      '抱抱脸': '🤗',
      'C站': '🎨',
      '魔搭社区': '🤖',
      'ModelScope': '🤖',
      '其他': '🔗'
    };
    return icons[platform] || '🔗';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      '百度网盘': '#1890ff',
      '夸克网盘': '#ff6b00',
      '阿里云盘': '#1890ff',
      '迅雷云盘': '#ff6b00',
      '123云盘': '#00a8ff',
      '直链地址': '#667eea',
      '抱抱脸': '#ffd700',
      'C站': '#6366f1',
      '魔搭社区': '#764ba2',
      'ModelScope': '#764ba2',
      '其他': '#999'
    };
    return colors[platform] || '#999';
  };

  return (
    <div className="downloads-container fade-in">
      <div className="page-header">
        <h1 className="page-title">资源中心</h1>
        <p className="page-subtitle">
          {videoId ? '该视频的相关资源' : selectedCategory ? `${selectedCategory} - 资源中心` : 'AI整合包和工具下载'}
        </p>
        {videoId && (
          <button 
            onClick={() => navigate('/downloads')}
            className="back-btn"
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            ← 查看所有资源
          </button>
        )}
        {selectedCategory && !videoId && (
          <button 
            onClick={handleBackToCategories}
            className="back-btn"
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            ← 返回分类列表
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">加载中...</div>
      ) : videoId ? (
        // 显示指定视频的资源（资源中心 + AI创作助手）
        downloads.length === 0 && aiLauncherForVideo.length === 0 ? (
          <div className="empty-state">
            <p>该视频暂无相关资源</p>
          </div>
        ) : (
          <div className="video-resources-unified">
            {downloads.length > 0 && (
              <div className="video-resources-section">
                <h2 className="video-resources-section-title">资源中心</h2>
                <div className="downloads-grid">
                  {downloads.map((download) => (
              <div key={download.id} id={`download-${download.id}`} className="download-card">
                <div className="download-header">
                  <div className="platform-badge" style={{ backgroundColor: getPlatformColor(download.platform) }}>
                    <span className="platform-icon">{getPlatformIcon(download.platform)}</span>
                    <span className="platform-name">{download.platform}</span>
                  </div>
                </div>
                <div className="download-content">
                  <h3 className="download-title">{download.title}</h3>
                  {download.description && (
                    <p className="download-description">{download.description}</p>
                  )}
                  <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                    <a 
                      href={download.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-btn"
                    >
                      {download.platform}下载
                    </a>
                    {download.official_url && (
                      <a 
                        href={download.official_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-btn"
                        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                      >
                        官方下载
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
                </div>
              </div>
            )}
            {aiLauncherForVideo.length > 0 && (
              <div className="video-resources-section">
                <h2 className="video-resources-section-title">AI创作助手</h2>
                <div className="downloads-grid">
                  {aiLauncherForVideo.map((resource) => (
                    <div key={`ai-${resource.id}`} id={`ai-launcher-${resource.id}`} className="download-card ai-launcher-card">
                      <div className="download-header">
                        <div className="platform-badge" style={{ backgroundColor: getPlatformColor(resource.platform) }}>
                          <span className="platform-icon">{getPlatformIcon(resource.platform)}</span>
                          <span className="platform-name">{resource.platform}</span>
                        </div>
                      </div>
                      <div className="download-content">
                        <h3 className="download-title">{resource.title}</h3>
                        {resource.description && (
                          <p className="download-description">{resource.description}</p>
                        )}
                        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-btn"
                          >
                            {resource.platform}下载
                          </a>
                          {resource.official_url && (
                            <a
                              href={resource.official_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-btn"
                              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                            >
                              官方下载
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      ) : selectedCategory === null ? (
        // 显示分类列表
        categories.length === 0 ? (
          <div className="empty-state">
            <p>暂无分类</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category, index) => (
              <div 
                key={index} 
                className="category-card"
                onClick={() => handleCategoryClick(category)}
              >
                <h3 className="category-title">{category}</h3>
                <p className="category-description">点击查看该分类下的资源</p>
              </div>
            ))}
          </div>
        )
      ) : (
        // 按视频分组显示
        Object.keys(groupedDownloads).length === 0 ? (
          <div className="empty-state">
            <p>该分类下暂无下载资源</p>
          </div>
        ) : (
          <div className="downloads-by-video">
            {Object.keys(groupedDownloads).map((vid) => {
              const videoDownloads = groupedDownloads[vid];
              const video = vid !== 'unassigned' ? videos.find(v => v.id === parseInt(vid)) : null;
              
              return (
                <div key={vid} id={`video-section-${vid}`} className="video-download-section">
                  <h2 className="video-section-title">
                    {video ? video.title : '通用资源'}
                    {video && (
                      <a 
                        href={`https://www.bilibili.com/video/${video.bvid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-video-link"
                      >
                        查看视频 →
                      </a>
                    )}
                  </h2>
                  <div className="downloads-grid">
                    {videoDownloads.map((download) => (
                      <div key={download.id} id={`download-${download.id}`} className="download-card">
                        <div className="download-header">
                          <div className="platform-badge" style={{ backgroundColor: getPlatformColor(download.platform) }}>
                            <span className="platform-icon">{getPlatformIcon(download.platform)}</span>
                            <span className="platform-name">{download.platform}</span>
                          </div>
                        </div>
                        <div className="download-content">
                          <h3 className="download-title">{download.title}</h3>
                          {download.description && (
                            <p className="download-description">{download.description}</p>
                          )}
                          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                            <a 
                              href={download.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-btn"
                            >
                              {download.platform}下载
                            </a>
                            {download.official_url && (
                              <a 
                                href={download.official_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="download-btn"
                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                              >
                                官方下载
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default Downloads;

